import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import {
  Box,
  CustomButton,
  CustomDatePicker,
  TitleComponent,
} from "ui-components";
import { formatLargeNumber, formatCount } from "ui-lib";
import { HorizontalCard, Amount, Label } from "../../components/common";
import WeeklyOverview from "../../components/common/graph/weekly-overview";
import DealBy from "./deal-by";
import TopIndustries from "./top-industries";
import { getAccumulatedFunding } from "ui-io/queries/get-accumulated-funding";
import ValueWithSkeleton from "../../components/common/value-with-skeleton";

// const currentYear = new Date().getFullYear().toString();

const InvestmentTrends = () => {
  const [selectedYear, setSelectYear] = useState<{
    label: string;
    value: string;
  }>({
    label: "All",
    value: "All",
  });
  const [open, setOpen] = useState(false);

  const accumulatedFundingConnection = useQuery(
    getAccumulatedFunding({ year: Number(selectedYear?.value) })
  );

  const accumulatedFunding: Record<string, any> = useMemo(() => {
    if (!accumulatedFundingConnection?.data) {
      return {};
    }

    const data = accumulatedFundingConnection.data;
    return {
      fundedAmount: data.fundedAmount,
      fundingRounds: data.fundingRounds,
      mostFundedStage: data.mostFundedStage,
      companyCount: data.companyCount,
    };
  }, [accumulatedFundingConnection.data]);

  return (
    <>
      <Box flexDir="row" justify="between">
        <TitleComponent fontSize="26px" marginTop={"0px"} className="mb-none">
          Investment Trends
        </TitleComponent>

        <div className="position-relative">
          <CustomButton
            onClick={() => setOpen(!open)}
            rightIcon="caret-down"
            bradius="5px"
            width="100px"
          >
            {selectedYear.value}
          </CustomButton>

          {open ? (
            <div className="inline-datepicker">
              <CustomDatePicker
                mode={"in_year"}
                yearItemNumber={5}
                value={selectedYear.value}
                startDate={null}
                endDate={null}
                showIcon={false}
                inline={true}
                onValueChange={(dates) => {
                  const year: string | undefined = dates[0]
                    ?.getFullYear()
                    .toString();
                  if (year) {
                    setSelectYear({ label: year, value: year });
                  }
                  setOpen(false);
                }}
              />
            </div>
          ) : null}
        </div>
      </Box>
      <Box>
        <WeeklyOverview
          title={"Range Text"}
          year={selectedYear?.value}
          isValueSymbol={false}
        />
        {/* <WeeklyOverview
          title={"Range Text"}
          graphData={[22, 30, 42, 25, 23, 69, 17, 39, 5, 12, 14, 73, 52]}
          graphLabel={[
            "Q1-22",
            "Q2-22",
            "Q3-22",
            "Q4-22",
            "Q1-23",
            "Q2-23",
            "Q3-23",
            "Q4-23",
            "Q1-24",
            "Q2-24",
          ]}
          isValueSymbol={false}
        /> */}
      </Box>
      <TitleComponent marginTop={"25px"}>Latest 12 Quarters</TitleComponent>

      <Box
        flexDir="row"
        items="center"
        justify="between"
        gap={"xlarge"}
        width={"1"}
        mt={"xlarge"}
      >
        <HorizontalCard
          avatarIcon={"/images/funding-round.svg"}
          chipStatus="danger"
          chipLabel={"15%"}
          imageWidth={"60"}
          className="pt-xxlg pb-lg pl-xlg"
        >
          <Label className="fs-16">Funding Rounds</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.fundingRounds || "89"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/total-funding.svg"}
          chipStatus="success"
          chipLabel={"15%"}
          imageWidth={"60"}
          className="pt-xxlg pb-lg pl-xlg"
        >
          <Label className="fs-16">Total Funding</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.fundedAmount
                  ? `£${formatLargeNumber(accumulatedFunding.fundedAmount)}`
                  : "£1.3 Bn"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/avg-round.svg"}
          chipStatus="success"
          chipLabel={"15%"}
          imageWidth={"60"}
          className="pt-xxlg pb-lg pl-xlg"
        >
          <Label className="fs-16">Most Funded Stage</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.mostFundedStage || "Early Stage"}
              </Amount>
            }
          />
        </HorizontalCard>

        <HorizontalCard
          avatarIcon={"/images/total-companies.svg"}
          chipStatus="success"
          chipLabel={"15%"}
          imageWidth={"60"}
          className="pt-xxlg pb-lg pl-xlg"
        >
          <Label className="fs-16">Total Companies</Label>
          <ValueWithSkeleton
            connection={accumulatedFundingConnection}
            children={
              <Amount className="fw-bold fs-26 mt-8">
                {accumulatedFunding.companyCount
                  ? formatCount(accumulatedFunding.companyCount)
                  : "89"}
              </Amount>
            }
          />
        </HorizontalCard>
      </Box>

      <DealBy />
      <TopIndustries />
    </>
  );
};

export default InvestmentTrends;